import React from 'react';
import { Link, useParams } from 'react-router-dom';
import styled from 'styled-components';

import api from '../api/axiosConfig';

const Page = styled.div`
  width: min(100%, 850px);
  margin: 24px auto;
  padding: 24px;
  border-radius: 26px;
  background: rgba(255,255,255,0.94);
  box-shadow: 0 16px 46px rgba(0,0,0,0.18);
`;

const Title = styled.h1`
  margin: 0 0 8px;
  color: #053969;
`;

const Box = styled.div`
  margin-top: 18px;
  padding: 18px;
  border-radius: 18px;
  background: rgba(5,57,105,0.06);
`;

const Label = styled.label`
  display: block;
  margin-top: 14px;
  font-weight: 800;
  color: #053969;
`;

const Input = styled.input`
  width: 100%;
  margin-top: 6px;
  padding: 12px;
  border: 1px solid rgba(5,57,105,0.25);
  border-radius: 12px;
  font-size: 16px;
`;

const Button = styled.button`
  margin-top: 18px;
  padding: 12px 18px;
  border: 0;
  border-radius: 12px;
  background: #053969;
  color: white;
  font-weight: 900;
  cursor: pointer;

  &:disabled {
    opacity: 0.6;
    cursor: default;
  }
`;

const Progress = styled.div`
  margin-top: 14px;
  height: 14px;
  overflow: hidden;
  border-radius: 999px;
  background: rgba(5,57,105,0.14);

  > div {
    height: 100%;
    width: ${(p) => Math.max(0, Math.min(100, p.$value || 0))}%;
    background: #053969;
    transition: width 0.4s ease;
  }
`;

const ErrorBox = styled.div`
  margin-top: 14px;
  padding: 12px;
  border-radius: 12px;
  background: rgba(220,38,38,0.1);
  color: #991b1b;
  font-weight: 700;
`;

const ACTIVE = new Set([
  'pending',
  'installing',
  'configuring_domain',
  'configuring_ssl',
  'starting_services',
]);

const LABELS = {
  pending: 'Waiting to start',
  installing: 'Installing',
  configuring_domain: 'Configuring domain',
  configuring_ssl: 'Configuring SSL',
  starting_services: 'Starting services',
  done: 'Installation complete',
  failed: 'Installation failed',
};

export default function AutoInstall() {
  const { orderId } = useParams();

  const [loading, setLoading] = React.useState(true);
  const [submitting, setSubmitting] = React.useState(false);
  const [meta, setMeta] = React.useState(null);
  const [job, setJob] = React.useState(null);
  const [error, setError] = React.useState('');

  const [sshHost, setSshHost] = React.useState('');
  const [sshPort, setSshPort] = React.useState('22');
  const [sshUser, setSshUser] = React.useState('root');
  const [sshPassword, setSshPassword] = React.useState('');
  const [adminUsername, setAdminUsername] = React.useState('admin');
  const [adminPassword, setAdminPassword] = React.useState('');

  const load = React.useCallback(async () => {
    try {
      const res = await api.get(`/licenses/${orderId}/install/`);
      setMeta(res.data);
      setJob(res.data?.installation || null);
      setError('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to load AutoInstall.');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  React.useEffect(() => {
    load();
  }, [load]);

  React.useEffect(() => {
    if (!job || !ACTIVE.has(job.status)) return undefined;

    const timer = window.setInterval(async () => {
      try {
        const res = await api.get(`/licenses/installations/${job.id}/`);
        setJob(res.data);
      } catch (e) {
        console.error(e);
      }
    }, 3000);

    return () => window.clearInterval(timer);
  }, [job]);

  const startInstall = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const res = await api.post(`/licenses/${orderId}/install/`, {
        target_domain: meta.licensed_domain,
        ssh_host: sshHost,
        ssh_port: Number(sshPort),
        ssh_user: sshUser,
        ssh_password: sshPassword,
        admin_username: adminUsername,
        admin_password: adminPassword,
      });

      setSshPassword('');
      setAdminPassword('');
      setJob(res.data);
    } catch (e) {
      const data = e?.response?.data;
      setError(
        data?.detail ||
        data?.ssh_host?.[0] ||
        data?.target_domain?.[0] ||
        data?.ssh_user?.[0] ||
        data?.ssh_password?.[0] ||
        'Unable to start AutoInstall.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const retry = async () => {
    setSubmitting(true);
    setError('');

    try {
      const res = await api.post(
        `/licenses/installations/${job.id}/retry/`
      );
      setJob((current) => ({
        ...current,
        ...res.data,
        error_message: '',
      }));
    } catch (e) {
      setError(e?.response?.data?.detail || 'Unable to retry installation.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <Page>Loading AutoInstall...</Page>;
  }

  return (
    <Page>
      <Title>AutoInstall</Title>
      <div>
        Install your purchased website automatically on your VPS.
      </div>

      <Box>
        <strong>Licensed domain:</strong>{' '}
        {meta?.licensed_domain || '—'}
      </Box>

      {!job ? (
        <form onSubmit={startInstall}>
          <Label>
            VPS IP address or hostname
            <Input
              value={sshHost}
              onChange={(e) => setSshHost(e.target.value)}
              required
              autoComplete="off"
            />
          </Label>

          <Label>
            SSH port
            <Input
              type="number"
              min="1"
              max="65535"
              value={sshPort}
              onChange={(e) => setSshPort(e.target.value)}
              required
            />
          </Label>

          <Label>
            SSH username
            <Input
              value={sshUser}
              onChange={(e) => setSshUser(e.target.value)}
              required
              autoComplete="username"
            />
          </Label>

          <Label>
            SSH password
            <Input
              type="password"
              value={sshPassword}
              onChange={(e) => setSshPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </Label>

          <Label>
            Website admin username
            <Input
              value={adminUsername}
              onChange={(e) => setAdminUsername(e.target.value)}
              required
              autoComplete="off"
            />
          </Label>

          <Label>
            Website admin password
            <Input
              type="password"
              minLength="10"
              value={adminPassword}
              onChange={(e) => setAdminPassword(e.target.value)}
              required
              autoComplete="new-password"
            />
          </Label>

          <Button type="submit" disabled={submitting}>
            {submitting ? 'Starting...' : 'Start AutoInstall'}
          </Button>
        </form>
      ) : (
        <Box>
          <strong>{LABELS[job.status] || job.status}</strong>

          <Progress $value={job.progress}>
            <div />
          </Progress>

          <div style={{ marginTop: 8 }}>
            {job.progress || 0}%
          </div>

          {job.status === 'done' ? (
            <div style={{ marginTop: 14 }}>
              ✅ Website installation completed.
            </div>
          ) : null}

          {job.status === 'failed' ? (
            <>
              <ErrorBox>
                {job.error_message || 'Installation failed.'}
              </ErrorBox>

              <Button
                type="button"
                onClick={retry}
                disabled={submitting}
              >
                {submitting ? 'Retrying...' : 'Retry installation'}
              </Button>

              <Button
                type="button"
                onClick={() => {
                  setJob(null);
                  setSshPassword('');
                  setError('');
                }}
                disabled={submitting}
              >
                Change connection details
              </Button>
            </>
          ) : null}

          {ACTIVE.has(job.status) ? (
            <div style={{ marginTop: 14 }}>
              You can close this page. Installation continues on the server.
            </div>
          ) : null}
        </Box>
      )}

      {error ? <ErrorBox>{error}</ErrorBox> : null}

      <div style={{ marginTop: 20 }}>
        <Link to="/me">← Back to My Account</Link>
      </div>
    </Page>
  );
}
